import { isMobile } from "@repo/shared-utils/mobile-utils";
import { getCookie, getPageType, isBrowser, getSessionData } from "@repo/shared-utils/common-utils";
import { cryptoAPICalledData } from "@repo/shared-utils/crypto";
import { CleverTapConstants } from "../constants/clevertap.constants";
import { clevertapEvent } from "./clevertapevent-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";

declare const window: {
  dataLayer: DataLayerType[];
  clevertap: any;
} & Window;

export type DataLayerType = Partial<{
  application_id: string;
  component: string;
  cta_text: string;
  customer_id: string;
  customer_tag: string;
  destination_url: string;
  element: string;
  event: string;
  event_type: string;
  field_name: string;
  field_value: string;
  form_name: string;
  journey_tag: string;
  page_type: string;
  page_url: string;
  product_category: string;
  product_name: string;
  section_title: string;
  stage_tag: string;
  sub_section: string;
  submission_status: string;
  tab_position: string;
  tag1: string;
  tag2: string;
  tag3: string;
  time_spent: string;
  validation_error: string;
  ad_spot_key: string;
  ad_id: string;
  error_code: number;
  error_type: string;
  ad_name: string;
  campaign_id: string;
}>;

type ConditionalKeys = {
  isBannerComponent?: string;
};

type CleverTapEventProps = Record<string, string>;
export type CombinedClevertapProps = CleverTapEventProps & ConditionalKeys;
const defaultDataLayer: DataLayerType = {
  application_id: "NA",
  component: "NA",
  cta_text: "NA",
  customer_id: "NA",
  customer_tag: "NA",
  destination_url: "NA",
  element: "NA",
  event: "NA",
  event_type: "NA",
  field_name: "NA",
  field_value: "NA",
  form_name: "NA",
  journey_tag: "NA",
  page_type: "NA",
  page_url: "NA",
  product_category: "NA",
  product_name: "NA",
  section_title: "NA",
  stage_tag: "NA",
  sub_section: "NA",
  submission_status: "NA",
  tab_position: "NA",
  tag1: "NA",
  tag2: "NA",
  tag3: "NA",
  time_spent: "NA",
  validation_error: "NA",
};

export type DataLayerProps = Omit<
  DataLayerType,
  | "page_url"
  | "page_type"
  | "tag1"
  | "tag2"
  | "tag3"
  | "customer_id"
  | "customer_tag"
  | "product_name"
  | "product_category"
>;

export const generateClevertapObject = (
  dataLayerObject: DataLayerType,
  cleverTapCustomProps: CombinedClevertapProps
): CleverTapEventProps => {
  // segregate the fields into complsory and optional fields
  // some fields are only applicable for banners

  // first add all the generic properties to clevertap Object
  const campaignCookie = getCookie("campaignCookie")
    ? JSON.parse(getCookie("campaignCookie") ?? "{}")
    : {};
  const LastClickCookie = getCookie("LastClickCookie")
    ? JSON.parse(getCookie("LastClickCookie") ?? "{}")
    : {};
  const gaID = getCookie("_ga") || "NA";
  let pageName =
    dataLayerObject.product_name && dataLayerObject.product_name !== "NA"
      ? `${dataLayerObject.product_name} ${dataLayerObject.page_type}`
      : dataLayerObject.product_category;
  const epSource = {
    action: "read",
    type: "string",
    key: "EP_SOURCE_PARENT",
    value: "",
  };
  const ep_source = isOpenInApp() && prompt(`SharedPref://${JSON.stringify(epSource)}`);
  const deviceType =
    isBrowser() && !isOpenInApp() && isMobile
      ? CleverTapConstants.MOBWEB
      : CleverTapConstants.DESKWEB;
  pageName = pageName?.replace(/-/g, " ").toUpperCase();
  let cleverTapObject: CleverTapEventProps = {
    EP_CTA: dataLayerObject.cta_text || "NA",
    EP_SECTION: (dataLayerObject.section_title as string) || "NA",
    EP_SOURCE: isOpenInApp() ? ep_source || "NA" : LastClickCookie?.click || "NA",
    EP_SUB_TEXT: dataLayerObject.section_title || "NA",
    EP_USER_TYPE:
      dataLayerObject.customer_tag === "NA"
        ? "NTB"
        : (dataLayerObject.customer_tag as string)?.toUpperCase(),
    EP_PAGE_NAME: pageName || "NA",
    EP_UTM_SOURCE: campaignCookie?.utm_source || "NA",
    EP_UTM_MEDIUM: campaignCookie.utm_medium || "NA",
    EP_UTM_CAMPAIGN: campaignCookie.utm_campaign || "NA",
    EP_PAGE_URL: window.location.href || "NA",
    EP_HOSTNAME: window.location.hostname || "NA",
    EP_UTM_CONTENT: campaignCookie?.utm_content || "NA",
    EP_LAST_CLICK: LastClickCookie?.click || "NA",
    EP_CT_ID: window.clevertap?.getCleverTapID?.() || "NA",
    DEVICE_TYPE: isOpenInApp() ? CleverTapConstants.DEVICEAPP : deviceType,
    EP_PLATFORM: isOpenInApp() ? CleverTapConstants.APP : CleverTapConstants.WEB,
    EP_BUSINESS: dataLayerObject.product_name || "NA",
    EP_SECTION_CTA: dataLayerObject.cta_text || "NA",
    EP_PRODUCT_NAME: dataLayerObject.product_name || "NA",
    EP_SUB_SECTION: dataLayerObject.sub_section || "NA",
    EP_LOGIN_STATUS: "non-logged-in",
    EP_GA_ID: gaID,
    EP_TIMESTAMP: new Date().toLocaleString() || "NA",
  };

  // add banner specific properties to the object based on isBanner flag
  if (cleverTapCustomProps?.isBannerComponent) {
    cleverTapObject = {
      ...cleverTapObject,
      EP_BANNER_CTA: dataLayerObject.cta_text || "NA",
      EP_BANNER_PRODUCT: dataLayerObject.product_name || "NA",
      EP_BANNER_SEQUENCE: dataLayerObject.tab_position || "NA",
      EP_BANNER_TEXT: dataLayerObject.section_title || "NA",
      EP_BANNER_MODULE_NAME: `${dataLayerObject.page_type}_${dataLayerObject.tag2}`,
      EP_BANNER_SUBDIVISION: dataLayerObject.section_title || "NA",
      EP_BANNER_TYPE: dataLayerObject.component || "NA",
      EP_BANNER_CLICK_TYPE: dataLayerObject.cta_text || "NA",
      EP_BANNER_DESCRIPTION: dataLayerObject.section_title || "NA",
      EP_BANNER_MODULE_SECTION:
        dataLayerObject.tag1 === "homepage"
          ? dataLayerObject.section_title || "NA"
          : `${dataLayerObject.tag1}_${dataLayerObject.component}`,
    };
  }

  // add custom or component specific properties to the object if existing in dataLayerObject
  cleverTapObject = {
    ...cleverTapObject,
    ...window.clevertap?.ctPageObject,
    ...cleverTapCustomProps,
  };
  return cleverTapObject;
};

/**
 * Data Layer and CleverTap integration function
 * @param props - Data layer properties
 * @param cleverTapCustomProps - Custom properties for CleverTap
 * @param preventCTEvent - Flag to prevent CleverTap event
 * @returns {Promise<void>}
 * @example
 * dataLayer({ event: "page_view", category: "home" });
 */
export const dataLayer = async (
  loginStatus: string,
  props: DataLayerProps,
  cleverTapCustomProps?: CombinedClevertapProps,
  preventCTEvent = false
): Promise<void> => {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  const page_url = window.location.href;
  const tag1 = getPageType("tag-1") || "NA";
  const tag2 = getPageType("tag-2") || "NA";
  const tag3 = getPageType("tag-3") || "NA";
  const page_type = tag1;
  const product_category = tag2;
  const product_name = tag3;

  const { ENABLE_CT = true } = (await cryptoAPICalledData()) || {};
  const clevertapClickEventName = cleverTapCustomProps?.EVENT_NAME ?? getPageType("tag-8");
  getSessionData()
    ?.then(({ custId, custType, dob }) => {
      let is_loggedin;
      if (dob && loginStatus === "success") {
        is_loggedin = "Logged-In";
      }
      const dataLayerObject = {
        ...defaultDataLayer,
        ...props,
        page_url,
        page_type,
        tag1,
        tag2,
        tag3,
        customer_id: custId,
        customer_tag: loginStatus === "failure" ? "NA" : custType.toLowerCase(),
        product_category,
        product_name,
        is_loggedin,
      };
      window.dataLayer.push(dataLayerObject);
      if (ENABLE_CT && clevertapClickEventName && !preventCTEvent) {
        const generatedClevertapObject = generateClevertapObject(
          dataLayerObject,
          cleverTapCustomProps as CombinedClevertapProps
        );
        clevertapEvent(clevertapClickEventName, generatedClevertapObject);
      }
    })
    .catch(() => {
      const dataLayerObject = {
        ...defaultDataLayer,
        ...props,
        page_url,
        page_type,
        tag1,
        tag2,
        tag3,
        product_category,
        product_name,
      };

      // Safety check for window object in test environments
      if (window?.dataLayer) {
        window.dataLayer.push(dataLayerObject);
      }

      if (ENABLE_CT && clevertapClickEventName) {
        const generatedClevertapObject = generateClevertapObject(
          dataLayerObject,
          cleverTapCustomProps as CombinedClevertapProps
        );
        clevertapEvent(clevertapClickEventName, generatedClevertapObject);
      }
    });
};

export const CTPageView = async (ctEventName: string) => {
  const { ENABLE_CT } = await cryptoAPICalledData();
  if (!ENABLE_CT) {
    return;
  }
  const tag1 = getPageType("tag-1");
  const tag2 = getPageType("tag-2");
  const tag3 = getPageType("tag-3");
  const page_type = tag1;
  const product_category = tag2;
  const product_name = tag3;
  const { custId, custType } = await getSessionData();

  const pageViewCt = (initialLoad?: boolean) => {
    const customPageProperties = window.clevertap?.ctPageObject || {};
    const generatedClevertapObject = generateClevertapObject(
      {
        page_type,
        product_category,
        product_name,
        customer_id: custId,
        customer_tag: custType,
      },
      {
        ...customPageProperties,
        EP_LOAD_TYPE: initialLoad ? "INITIAL LOADED" : "FULLY LOADED",
      }
    );
    clevertapEvent(ctEventName, generatedClevertapObject);
  };
  setTimeout(pageViewCt, 5000);
  pageViewCt(true);
};

if (typeof document !== "undefined") {
  const cleverTapPageView = getPageType("tag-7");
  if (cleverTapPageView) {
    CTPageView(cleverTapPageView);
  }
}
